import { Box, Hidden, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { numberFormat } from "../../../helpers/numberFormat";

export const LPTitleDataSmall = ({ fund, url }) => {
  const { t } = useTranslation("global");
  const total = fund.total;
  const amount = fund.amount;
  const percentagePaid = fund.fund.percentagePaid || 0;
  const percentageDistributed = fund.fund.percentageDistributed || 0;
  const portfolioValue = fund.portfolioValue || 0;

  return (
    <Hidden smUp>
      <Stack width="100%" gap={0.1}>
        <Typography
          color="primary"
          textAlign="center"
          variant="body2"
          sx={{ fontWeight: "bold" }}
        >
          "{fund.fund.name}"
        </Typography>
        <Stack direction="row" flexWrap="wrap" justifyContent="space-around">
          <Stack direction="row" gap={1} alignItems="center">
            <Typography
              color="primary"
              variant="caption"
              sx={{ fontWeight: "bold" }}
            >
              {t("latentIvestments.fund")}
            </Typography>
            <Box
              sx={{
                backgroundColor: "secondary.main",
                height: "13px",
                width: "13px",
                borderRadius: "50%",
              }}
            ></Box>
          </Stack>
          <Stack direction="row" gap={1} alignItems="center">
            <Typography
              color="primary"
              variant="caption"
              sx={{ fontWeight: "bold" }}
            >
              "{fund.whoInvest}"
            </Typography>
            <Box
              sx={{
                backgroundColor: "green",
                height: "13px",
                width: "13px",
                borderRadius: "50%",
              }}
            ></Box>
          </Stack>
        </Stack>
        <Stack padding="0 5px">
          <Typography color="primary" variant="caption" textAlign="center">
            {t("latentIvestments.amountCommitted")}:
          </Typography>
          <Stack direction="row" justifyContent="space-around">
            <Typography color="secondary" variant="caption" textAlign="center">
              {numberFormat(total)}
            </Typography>
            <Typography color="green" variant="caption" textAlign="center">
              {numberFormat(amount)}
            </Typography>
          </Stack>
        </Stack>
        <Stack padding="0 5px">
          <Typography color="primary" variant="caption" textAlign="center">
            {t("latentIvestments.participationFund")}:
          </Typography>
          <Stack direction="row" justifyContent="space-around">
            <Typography color="secondary" variant="caption" textAlign="center">
              100%
            </Typography>
            <Typography color="green" variant="caption" textAlign="center">
              {((amount * 100) / total).toFixed(2)}%
            </Typography>
          </Stack>
        </Stack>
        <Stack padding="0 5px">
          <Typography color="primary" variant="caption" textAlign="center">
            {t("latentIvestments.percentage")}:
          </Typography>
          <Stack direction="row" justifyContent="space-around">
            <Typography color="secondary" variant="caption" textAlign="center">
              {percentagePaid}%
            </Typography>
            <Typography color="green" variant="caption" textAlign="center">
              {percentagePaid}%
            </Typography>
          </Stack>
        </Stack>
        <Stack padding="0 5px">
          <Typography color="primary" variant="caption" textAlign="center">
            {t("latentIvestments.amountOfPaid")}:
          </Typography>
          <Stack direction="row" justifyContent="space-around">
            <Typography color="secondary" variant="caption" textAlign="center">
              {numberFormat(total * (percentagePaid / 100))}
            </Typography>
            <Typography color="green" variant="caption" textAlign="center">
              {numberFormat(amount * (percentagePaid / 100))}
            </Typography>
          </Stack>
        </Stack>
        <Stack padding="0 5px">
          <Typography color="primary" variant="caption" textAlign="center">
            {t("latentIvestments.percentageDistributed")}:
          </Typography>
          <Stack direction="row" justifyContent="space-around">
            <Typography color="secondary" variant="caption" textAlign="center">
              {Number(percentageDistributed).toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%
            </Typography>
            <Typography color="green" variant="caption" textAlign="center">
              {Number(percentageDistributed).toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%
            </Typography>
          </Stack>
        </Stack>
        <Stack padding="0 5px">
          <Typography color="primary" variant="caption" textAlign="center">
            {t("latentIvestments.amountDistributed")}:
          </Typography>
          <Stack direction="row" justifyContent="space-around">
            <Typography color="secondary" variant="caption" textAlign="center">
              {numberFormat(total * (percentageDistributed / 100))}
            </Typography>
            <Typography color="green" variant="caption" textAlign="center">
              {numberFormat(amount * (percentageDistributed / 100))}
            </Typography>
          </Stack>
        </Stack>
        <Stack padding="0 5px">
          <Typography color="primary" variant="caption" textAlign="center">
            {t("latentIvestments.dpi")}:
          </Typography>
          <Stack direction="row" justifyContent="space-around">
            <Typography color="secondary" variant="caption" textAlign="center">
              {percentagePaid ? (percentageDistributed / percentagePaid).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " x" : "0,00 x"}
            </Typography>
            <Typography color="green" variant="caption" textAlign="center">
              {percentagePaid ? (percentageDistributed / percentagePaid).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " x" : "0,00 x"}
            </Typography>
          </Stack>
        </Stack>
        <Stack padding="0 5px">
          <Typography color="primary" variant="caption" textAlign="center">
            {t("latentIvestments.rvpi")}:
          </Typography>
          <Stack direction="row" justifyContent="space-around">
            <Typography color="secondary" variant="caption" textAlign="center">
              {(total * (percentagePaid / 100)) ? (portfolioValue / (total * (percentagePaid / 100))).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " x" : "0,00 x"}
            </Typography>
            <Typography color="green" variant="caption" textAlign="center">
              {(total * (percentagePaid / 100)) ? (portfolioValue / (total * (percentagePaid / 100))).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " x" : "0,00 x"}
            </Typography>
          </Stack>
        </Stack>
        <Stack padding="0 5px">
          <Typography color="primary" variant="caption" textAlign="center">
            {t("latentIvestments.tvpi")}:
          </Typography>
          <Stack direction="row" justifyContent="space-around">
            <Typography color="secondary" variant="caption" textAlign="center">
              {(() => {
                const dpi = percentagePaid ? (percentageDistributed / percentagePaid) : 0;
                const rvpi = (total * (percentagePaid / 100)) ? (portfolioValue / (total * (percentagePaid / 100))) : 0;
                return (dpi + rvpi).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " x";
              })()}
            </Typography>
            <Typography color="green" variant="caption" textAlign="center">
              {(() => {
                const dpi = percentagePaid ? (percentageDistributed / percentagePaid) : 0;
                const rvpi = (total * (percentagePaid / 100)) ? (portfolioValue / (total * (percentagePaid / 100))) : 0;
                return (dpi + rvpi).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " x";
              })()}
            </Typography>
          </Stack>
        </Stack>
        {url?.urlVideo && (
          <Stack
            direction="row"
            gap={2}
            padding="0 5px"
            justifyContent="center"
          >
            <Typography color="primary" variant="caption">
              {t("latentIvestments.fundURL")}:{" "}
            </Typography>
            <Typography color="secondary" variant="caption" textAlign="center">
              {" "}
              <a
                style={{ color: "#EA7F22" }}
                href={url?.urlVideo}
                target="_blank"
                rel="noreferrer"
              >
                {t("link.link")}
              </a>
            </Typography>
          </Stack>
        )}
      </Stack>
    </Hidden>
  );
};
